import { RegFieldName } from "models/application_info";
import { SessionInfo } from "models/session_info";
import { $t, t_key } from "plugins/i18n";
import { validatePersonCode } from "plugins/person-code-helper";
import { validatePhoneNumber } from "plugins/validation";
import * as yup from "yup";

export function fieldValuesFields() {
  return {
    field_values: {
      birthday: "",
      email: "",
      mobile_phone: "",
      first_name: "",
      last_name: "",
      person_code: "",
      address: "",
      area: "",
      profession: "",
      local_gov: "",
      local_gov_date: ""
    }
  }
}

export function fieldValuesValidationSchema(session: SessionInfo, registerUnderage = false) {
  const systemManagedRegisterFields = new Set<string>([
    RegFieldName.LocalGov,
    RegFieldName.LocalGovDate,
  ]);

  const hasValue = (fieldType: string, value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (fieldType === "single_choice_dropdown" && typeof value === "object") {
      if (typeof value.value === "string") return value.value.trim().length > 0;
      return false;
    }
    if (fieldType === "toggle" || fieldType === "single_choice_checkbox") {
      return true;
    }
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
  };

  const createFieldSchema = (field: { name: string; type: string; required: boolean }): yup.AnySchema => {
    let schema: yup.AnySchema;
    switch (field.type) {
      case "email":
        schema = yup.string().email($t(t_key.validation.invalid_email));
        break;
      case "date":
        schema = yup.mixed().test("validDate", $t(t_key.validation.invalid_date), (value) => {
          if (!hasValue(field.type, value)) return true;
          if (value instanceof Date) return !Number.isNaN(value.getTime());
          if (typeof value === "string") return !Number.isNaN(Date.parse(value));
          return false;
        });
        break;
      case "person_code":
        schema = yup.string().test("validPersonCode", $t(t_key.validation.invalid_person_code), (value) => {
          if (!hasValue(field.type, value)) return true;
          if (typeof value !== "string") return false;
          return validatePersonCode(value);
        });
        break;
      default:
        schema = yup.mixed();
        break;
    }

    if (field.name === RegFieldName.MobilePhone) {
      schema = yup.string().test("validNumber", $t(t_key.validation.invalid_phone_number), (value) => {
        if (!hasValue(field.type, value)) return true;
        return validatePhoneNumber(value as string).isValid;
      });
    }

    if (field.required && !systemManagedRegisterFields.has(field.name)) {
      schema = schema.test(
        `required_${field.name}`,
        $t(t_key.validation.required_field),
        (value) => hasValue(field.type, value)
      );
    }

    return schema;
  };

  const dynamicFieldValidations = (session.registration?.regFields ?? []).reduce((acc, field) => {
    acc[field.name] = createFieldSchema(field);
    return acc;
  }, {} as Record<string, yup.AnySchema>);

  const withUnderageRequired = (fieldName: string) =>
    (dynamicFieldValidations[fieldName] ?? yup.mixed()).test(
      `underage_required_${fieldName}`,
      $t(t_key.validation.required_field),
      (value) => {
        if (!registerUnderage) return true;
        return hasValue("string", value);
      }
    );

  return {
    ...dynamicFieldValidations,
    first_name: withUnderageRequired(RegFieldName.FirstName),
    last_name: withUnderageRequired(RegFieldName.LastName)
  };
}
